/**
 * Hook para gestionar el proceso manual completo de Envía Colvanes
 * Orquesta la consulta de pedidos, extracción de guías y procesamiento individual
 */
import { useState, useCallback } from 'react'
import { ColvanesManualService } from '../../services/cron/colvanesManualService'
import { ColvanesTrackUpdateService } from '../../services/cron/colvanesTrackUpdateService'
import type {
  ColvanesManualProcessPhase,
  ColvanesManualProcessProgress,
  ColvanesManualProcessResults,
  OrderWithGuide,
} from '../../types/cron/colvanesManual'

interface UseColvanesManualProcessReturn {
  isProcessing: boolean
  currentPhase: ColvanesManualProcessPhase['phase']
  progress: ColvanesManualProcessProgress
  results: ColvanesManualProcessResults
  error: string | null
  execute: () => Promise<void>
  reset: () => void
}

const INITIAL_PROGRESS: ColvanesManualProcessProgress = {
  ordersFetched: 0,
  totalOrders: 0,
  currentPage: 0,
  totalPages: 0,
  guidesExtracted: 0,
  guidesProcessed: 0,
  totalGuides: 0,
  currentGuide: 0,
}

const INITIAL_RESULTS: ColvanesManualProcessResults = {
  updated: [],
  notUpdated: [],
  summary: {
    totalGuides: 0,
    updated_count: 0,
    not_delivered_count: 0,
    not_found_count: 0,
    skipped_count: 0,
    delivered_count: 0,
    error_count: 0,
  },
}

/**
 * Consolidar resultados del procesamiento
 */
function consolidateResults(
  orders: OrderWithGuide[],
  trackUpdateResponse: {
    guides_queried: number
    guides_processed: number
    updated_count: number
    skipped_count: number
    not_found_count: number
    not_delivered_count: number
    delivered_count: number
    error_count: number
    details: Array<{
      guia: string
      estado_guia: string | null
      action: string
      reason: string
      order_updated?: {
        success: boolean
        order_id: string
        previous_status: string
        new_status: string
        error: string | null
      }
    }>
  }
): ColvanesManualProcessResults {
  // Crear mapa de guía -> order para búsqueda rápida
  const guideToOrderMap = new Map<string, OrderWithGuide>()
  for (const order of orders) {
    guideToOrderMap.set(order.guia, order)
  }

  const updated: ColvanesManualProcessResults['updated'] = []
  const notUpdated: ColvanesManualProcessResults['notUpdated'] = []

  // Procesar cada detalle de la respuesta
  for (const detail of trackUpdateResponse.details) {
    const order = guideToOrderMap.get(detail.guia)

    if (!order) {
      // No debería pasar, pero por si acaso
      continue
    }

    if (detail.action === 'updated' && detail.order_updated?.success) {
      updated.push({
        guia: detail.guia,
        orders_id: order.orders_id,
        id: order.id,
        store_name: order.store_name,
        estado_guia: detail.estado_guia || undefined,
        previous_status: detail.order_updated.previous_status,
        new_status: detail.order_updated.new_status,
      })
    } else {
      notUpdated.push({
        guia: detail.guia,
        orders_id: order.orders_id,
        id: order.id,
        store_name: order.store_name,
        reason: detail.reason,
        estado_guia: detail.estado_guia || undefined,
        action: detail.action,
      })
    }
  }

  return {
    updated,
    notUpdated,
    summary: {
      totalGuides: trackUpdateResponse.guides_queried,
      updated_count: trackUpdateResponse.updated_count,
      not_delivered_count: trackUpdateResponse.not_delivered_count,
      not_found_count: trackUpdateResponse.not_found_count,
      skipped_count: trackUpdateResponse.skipped_count,
      delivered_count: trackUpdateResponse.delivered_count,
      error_count: trackUpdateResponse.error_count,
    },
  }
}

export function useColvanesManualProcess(): UseColvanesManualProcessReturn {
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentPhase, setCurrentPhase] =
    useState<ColvanesManualProcessPhase['phase']>('idle')
  const [progress, setProgress] =
    useState<ColvanesManualProcessProgress>(INITIAL_PROGRESS)
  const [results, setResults] =
    useState<ColvanesManualProcessResults>(INITIAL_RESULTS)
  const [error, setError] = useState<string | null>(null)

  /**
   * Ejecutar el proceso completo
   */
  const execute = useCallback(async () => {
    setIsProcessing(true)
    setError(null)
    setCurrentPhase('fetching_orders')
    setProgress(INITIAL_PROGRESS)
    setResults(INITIAL_RESULTS)

    try {
      // FASE 1: Consultar pedidos y extraer guías
      const orders = await ColvanesManualService.getAllColvanesOrders(
        (fetchProgress) => {
          setProgress((prev) => ({
            ...prev,
            currentPage: fetchProgress.currentPage,
            totalPages: fetchProgress.totalPages,
            ordersFetched: fetchProgress.ordersFetched,
            totalOrders: fetchProgress.ordersFetched, // Aproximación
          }))
        }
      )

      setProgress((prev) => ({
        ...prev,
        totalOrders: orders.length,
        guidesExtracted: orders.length,
        totalGuides: orders.length,
      }))

      // Validar que haya guías para procesar
      if (orders.length === 0) {
        setCurrentPhase('completed')
        setResults({
          updated: [],
          notUpdated: [],
          summary: {
            totalGuides: 0,
            updated_count: 0,
            not_delivered_count: 0,
            not_found_count: 0,
            skipped_count: 0,
            delivered_count: 0,
            error_count: 0,
          },
        })
        setIsProcessing(false)
        return
      }

      // FASE 2: Procesar guías individualmente
      setCurrentPhase('processing_guides')
      const trackingNumbers = orders.map((order) => order.guia)

      const trackUpdateResponse = await ColvanesTrackUpdateService.processGuides(
        trackingNumbers,
        (guideProgress) => {
          setProgress((prev) => ({
            ...prev,
            currentGuide: guideProgress.currentGuide,
            guidesProcessed: guideProgress.processedGuides,
          }))
        }
      )

      // FASE 3: Consolidar resultados
      const consolidatedResults = consolidateResults(
        orders,
        trackUpdateResponse
      )

      setResults(consolidatedResults)
      setCurrentPhase('completed')
    } catch (err: any) {
      console.error('Error en proceso manual de Colvanes:', err)
      setError(
        err.message || 'Error desconocido durante el proceso manual de Colvanes'
      )
      setCurrentPhase('error')
    } finally {
      setIsProcessing(false)
    }
  }, [])

  /**
   * Resetear estado
   */
  const reset = useCallback(() => {
    setIsProcessing(false)
    setCurrentPhase('idle')
    setProgress(INITIAL_PROGRESS)
    setResults(INITIAL_RESULTS)
    setError(null)
  }, [])

  return {
    isProcessing,
    currentPhase,
    progress,
    results,
    error,
    execute,
    reset,
  }
}

